
"use client";

import { useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to detect user inactivity and trigger a callback.
 * @param onIdle - Callback function to execute when the user is idle.
 * @param timeout - The inactivity timeout in milliseconds. If null, the hook is disabled.
 */
export function useIdleTimeout(onIdle: () => void, timeout: number | null) {
  const timeoutIdRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const resetTimer = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    if (timeout !== null) {
      timeoutIdRef.current = setTimeout(onIdle, timeout);
    }
  }, [onIdle, timeout]);

  const handleEvent = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (timeout === null) {
      // If timeout is null, do nothing and clean up any existing timers/listeners
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      return;
    }

    const events: (keyof WindowEventMap)[] = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart'];
    
    // Set initial timer
    resetTimer();

    // Add event listeners
    events.forEach(event => window.addEventListener(event, handleEvent));

    // Cleanup function
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      events.forEach(event => window.removeEventListener(event, handleEvent));
    };
  }, [handleEvent, resetTimer, timeout]);
}
